/**
 * Client-LOCAL tables — persisted beside the sync entities on both clients,
 * never synced. They hold derived state the device rebuilds from its own
 * rows, so they have no entity, no wire, no server twin.
 *
 * One registry, three consumers, so the two platforms can never disagree on a
 * table's name or key:
 *   - RN typed-table router: `buildStorageDescriptors(entities, LOCAL_TABLE_EXTRAS)`
 *     gives each table a route (and `resolveIndex` for its PK columns).
 *   - RN op-sqlite migrator: the same descriptors feed the idempotent
 *     `CREATE TABLE IF NOT EXISTS` layer that runs on every boot — no
 *     hand-written migration.
 *   - Web Dexie schema: `indexedDbStoresForLocalTables()` is the `.stores()`
 *     spec of the version band that declares them.
 *
 * A key here must NOT collide with an `entitySchema` route key (pinned in
 * `local-tables.spec.ts`): `buildStorageDescriptors` lets an extra overwrite an
 * entity's descriptor, and that would silently re-point a synced table.
 */
import type { ExtraStorageInput } from './storage-layout';

/** Route key of the per-deck study summary table (`SyncStorage` `table` arg). */
export const DECK_STUDY_SUMMARY_TABLE = 'deckStudySummary';

/** Route key of the per-(group, deck) study-group card table. */
export const SG_DECK_CARDS_TABLE = 'sgDeckCards';

/**
 * A local table's registry entry. `dexieBand` is the web schema version BASE
 * the store is declared in (`db.ts` adds `ENTITY_SCHEMA_VERSION`). Dexie only
 * runs upgrades for versions above the one a DB is already at, so a store added
 * to an EXISTING band never appears on an upgraded DB: every NEW local table
 * takes a NEW, higher band. RN ignores the band (its migrator is idempotent
 * `CREATE TABLE IF NOT EXISTS` on every boot). `dexieIndexes` are secondary
 * indexes web needs for `getByIndex` / `removeByIndex` on a non-PK column; on
 * RN a PK-prefix column is covered by the PK index (`resolveIndex`), so the
 * same reads need nothing there.
 */
export type LocalTableInput = ExtraStorageInput & {
  dexieBand: number;
  dexieIndexes?: readonly string[];
};

export const LOCAL_TABLE_EXTRAS: Readonly<Record<string, LocalTableInput>> =
  Object.freeze({
    /**
     * One `StoredDeckSummary` per deck (`sync-client/deck-summary`): the counts
     * and sorted timestamp arrays Home reads instead of the deck's card bodies.
     * Keyed by the deck's sync id; a deck's row is dropped with the deck.
     */
    [DECK_STUDY_SUMMARY_TABLE]: {
      tableName: 'deck_study_summary_v2',
      pk: ['deckId'],
      dexieBand: 300,
    },
    /**
     * One `StoredSGDeckCards` row per (group, deck): a study group's card
     * bodies, split out of the group snapshot (`sgGroupSnapshots`, which keeps
     * meta / members / folders / deckProps) so a card frame re-persists one
     * deck, not the group, and boot decodes one small blob per deck. Read per
     * group (`getByIndex(…, 'groupId', id)` — a PK-prefix on RN, the `groupId`
     * index on web); dropped with the deck or the group.
     */
    [SG_DECK_CARDS_TABLE]: {
      tableName: 'sg_deck_cards_v2',
      pk: ['groupId', 'deckId'],
      dexieBand: 400,
      dexieIndexes: ['groupId'],
    },
  });

function dexieStoreSpec(input: LocalTableInput): string {
  const pk = input.pk.length === 1 ? `&${input.pk[0]}` : `&[${input.pk.join('+')}]`;
  return [pk, ...(input.dexieIndexes ?? [])].join(', ');
}

/**
 * Dexie `.stores()` spec for the local tables of ONE band: `'&pk'` for a
 * single-column key, `'&[a+b]'` for a compound one, then any secondary
 * indexes — the same shape the entity-derived band uses, so a local table is
 * declared exactly like a synced one. Without `band` every table is returned
 * (RN-side callers and specs); `db.ts` declares one version per band via
 * `indexedDbLocalTableBands()`.
 */
export function indexedDbStoresForLocalTables(
  extras: Readonly<Record<string, LocalTableInput>> = LOCAL_TABLE_EXTRAS,
  band?: number,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [routeKey, input] of Object.entries(extras)) {
    if (band !== undefined && input.dexieBand !== band) continue;
    out[routeKey] = dexieStoreSpec(input);
  }
  return out;
}

/**
 * The Dexie version bands the local tables occupy, ascending, each with its
 * `.stores()` spec. `db.ts` declares `version(band + ENTITY_SCHEMA_VERSION)`
 * for each, so adding a registry entry with a new band is the whole web-side
 * schema change.
 */
export function indexedDbLocalTableBands(
  extras: Readonly<Record<string, LocalTableInput>> = LOCAL_TABLE_EXTRAS,
): Array<{ band: number; stores: Record<string, string> }> {
  const bands = Array.from(new Set(Object.values(extras).map((e) => e.dexieBand))).sort(
    (a, b) => a - b,
  );
  return bands.map((band) => ({
    band,
    stores: indexedDbStoresForLocalTables(extras, band),
  }));
}
